import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import adminApi from '../services/adminApi';
import {
  GST_STATES,
  formatStateLabel,
  resolveOrgStateCode,
  stateCodeFromGstin,
} from '../utils/gstStates';

const EMPTY_ISSUER = {
  orgName: '',
  orgEmail: '',
  orgPhone: '',
  orgWebsite: '',
  orgGstin: '',
  orgStateCode: '',
  orgAddress: '',
  orgLogoUrl: '',
  gstRate: '0.18',
  defaultLeadsFree: '',
  defaultNoteMinutesFree: '',
  defaultLeadRate: '',
  defaultNoteMinuteRate: '',
  referralLeadsFree: '',
  referralNoteMinutesFree: '',
};

const RECHARGE_TYPES = new Set(['TOPUP', 'WELCOME_GRANT', 'ADMIN_ADJUST', 'REFERRAL_GRANT']);
const USAGE_TYPES = new Set(['LEAD_GENERATED', 'NOTE_MINUTES']);

function formatMoney(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatGstBreakup(breakup) {
  if (!breakup || typeof breakup !== 'object') return '-';
  const parts = [];
  if (Number(breakup.cgst) > 0) parts.push(`CGST ${formatMoney(breakup.cgst)}`);
  if (Number(breakup.sgst) > 0) parts.push(`SGST ${formatMoney(breakup.sgst)}`);
  if (Number(breakup.igst) > 0) parts.push(`IGST ${formatMoney(breakup.igst)}`);
  return parts.length ? parts.join(' · ') : '-';
}

function formatGstPct(gstRate) {
  const n = Number(gstRate || 0);
  if (!Number.isFinite(n)) return '0.00%';
  return `${(n > 1 ? n : n * 100).toFixed(2)}%`;
}

function AdminModal({ title, subtitle, onClose, children, footer, wide }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className={`modal-content${wide ? ' large' : ''} admin-leads-billing-modal`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="leads-billing-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-leads-billing-modal-header">
          <div>
            <h3 id="leads-billing-modal-title">{title}</h3>
            {subtitle ? <p className="muted">{subtitle}</p> : null}
          </div>
          <button type="button" className="btn-secondary small" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="admin-leads-billing-modal-body">{children}</div>
        {footer ? <div className="admin-leads-billing-modal-footer">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}

function PlatformField({ label, children, full }) {
  return (
    <label className={`admin-leads-platform-field${full ? ' is-full' : ''}`}>
      <span className="admin-leads-platform-field-label">{label}</span>
      {children}
    </label>
  );
}

export default function AdminLeadsBilling({ onError }) {
  const [subTab, setSubTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkForm, setBulkForm] = useState({
    leadsFreeRemaining: '',
    noteMinutesFreeRemaining: '',
    creditBalance: '',
    reason: '',
  });

  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerCompany, setLedgerCompany] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerTab, setLedgerTab] = useState('recharge');

  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);

  const [issuer, setIssuer] = useState(EMPTY_ISSUER);
  const [issuerLoading, setIssuerLoading] = useState(false);
  const [issuerSaving, setIssuerSaving] = useState(false);
  const [issuerModalOpen, setIssuerModalOpen] = useState(false);
  const [issuerMessage, setIssuerMessage] = useState('');

  const loadIssuer = useCallback(async () => {
    setIssuerLoading(true);
    try {
      const data = await adminApi.fetchPlatformConfig();
      const cfg = data?.config || data || {};
      const gstin = String(cfg.orgGstin || '');
      setIssuer({
        orgName: String(cfg.orgName || ''),
        orgEmail: String(cfg.orgEmail || ''),
        orgPhone: String(cfg.orgPhone || ''),
        orgWebsite: String(cfg.orgWebsite || ''),
        orgGstin: gstin,
        orgStateCode: resolveOrgStateCode(gstin, cfg.orgStateCode),
        orgAddress:
          typeof cfg.orgAddress === 'string'
            ? cfg.orgAddress
            : cfg.orgAddress
              ? JSON.stringify(cfg.orgAddress)
              : '',
        orgLogoUrl: String(cfg.orgLogoUrl || ''),
        gstRate:
          cfg.gstRate === null || cfg.gstRate === undefined
            ? '0.18'
            : String(cfg.gstRate),
        defaultLeadsFree:
          cfg.defaultLeadsFree === null || cfg.defaultLeadsFree === undefined
            ? ''
            : String(cfg.defaultLeadsFree),
        defaultNoteMinutesFree:
          cfg.defaultNoteMinutesFree === null || cfg.defaultNoteMinutesFree === undefined
            ? ''
            : String(cfg.defaultNoteMinutesFree),
        defaultLeadRate:
          cfg.defaultLeadRate === null || cfg.defaultLeadRate === undefined
            ? ''
            : String(cfg.defaultLeadRate),
        defaultNoteMinuteRate:
          cfg.defaultNoteMinuteRate === null || cfg.defaultNoteMinuteRate === undefined
            ? ''
            : String(cfg.defaultNoteMinuteRate),
        referralLeadsFree:
          cfg.referralLeadsFree === null || cfg.referralLeadsFree === undefined
            ? ''
            : String(cfg.referralLeadsFree),
        referralNoteMinutesFree:
          cfg.referralNoteMinutesFree === null || cfg.referralNoteMinutesFree === undefined
            ? ''
            : String(cfg.referralNoteMinutesFree),
      });
    } catch (err) {
      onError?.(err?.response?.data?.message || err.message || 'Failed to load issuer profile');
    } finally {
      setIssuerLoading(false);
    }
  }, [onError]);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.fetchLeadsBillingAccounts();
      setAccounts(Array.isArray(data?.accounts) ? data.accounts : []);
      setMetrics(data?.metrics || null);
      setDefaults(data?.defaults || null);
      const nextEdits = {};
      (data?.accounts || []).forEach((row) => {
        nextEdits[row.companyId] = {
          leadsFreeRemaining: String(row.leadsFreeRemaining ?? 0),
          noteMinutesFreeRemaining: String(row.noteMinutesFreeRemaining ?? 0),
          creditBalance: String(row.creditBalance ?? 0),
          leadRate: row.leadRate == null ? '' : String(row.leadRate),
          noteMinuteRate: row.noteMinuteRate == null ? '' : String(row.noteMinuteRate),
          reason: '',
        };
      });
      setEdits(nextEdits);
      setSelectedIds(new Set());
    } catch (err) {
      onError?.(err?.response?.data?.message || err.message || 'Failed to load leads billing');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  const loadPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const data = await adminApi.fetchLeadsBillingPayments({
        status: paymentStatus || undefined,
        limit: 100,
      });
      setPayments(Array.isArray(data?.payments) ? data.payments : []);
    } catch (err) {
      onError?.(err?.response?.data?.message || err.message || 'Failed to load payments');
    } finally {
      setPaymentsLoading(false);
    }
  }, [onError, paymentStatus]);

  useEffect(() => {
    if (subTab === 'accounts') loadAccounts();
    if (subTab === 'payments') loadPayments();
  }, [subTab, loadAccounts, loadPayments]);

  useEffect(() => {
    loadIssuer();
  }, [loadIssuer]);

  const updateIssuerField = (field, value) => {
    setIssuer((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'orgGstin') {
        const code = stateCodeFromGstin(value);
        if (code) next.orgStateCode = code;
      }
      return next;
    });
    setIssuerMessage('');
  };

  const saveIssuer = async () => {
    setIssuerSaving(true);
    setIssuerMessage('');
    try {
      let gstRate = issuer.gstRate;
      if (gstRate !== '' && gstRate != null) {
        const n = Number(gstRate);
        if (Number.isFinite(n) && n > 1) gstRate = String(n / 100);
      }
      await adminApi.updatePlatformConfig({
        orgName: issuer.orgName,
        orgEmail: issuer.orgEmail,
        orgPhone: issuer.orgPhone,
        orgWebsite: issuer.orgWebsite,
        orgGstin: issuer.orgGstin,
        orgStateCode: issuer.orgStateCode,
        orgAddress: issuer.orgAddress,
        orgLogoUrl: issuer.orgLogoUrl,
        gstRate: gstRate === '' ? null : Number(gstRate),
        defaultLeadsFree:
          issuer.defaultLeadsFree === '' ? null : Number(issuer.defaultLeadsFree),
        defaultNoteMinutesFree:
          issuer.defaultNoteMinutesFree === ''
            ? null
            : Number(issuer.defaultNoteMinutesFree),
        defaultLeadRate:
          issuer.defaultLeadRate === '' ? null : Number(issuer.defaultLeadRate),
        defaultNoteMinuteRate:
          issuer.defaultNoteMinuteRate === ''
            ? null
            : Number(issuer.defaultNoteMinuteRate),
        referralLeadsFree:
          issuer.referralLeadsFree === '' ? null : Number(issuer.referralLeadsFree),
        referralNoteMinutesFree:
          issuer.referralNoteMinutesFree === ''
            ? null
            : Number(issuer.referralNoteMinutesFree),
      });
      setIssuerMessage('Issuer & leads defaults saved');
      await loadIssuer();
      await loadAccounts();
      setIssuerModalOpen(false);
    } catch (err) {
      onError?.(err?.response?.data?.message || err.message || 'Failed to save issuer');
    } finally {
      setIssuerSaving(false);
    }
  };

  const openIssuerModal = async () => {
    setIssuerMessage('');
    setIssuerModalOpen(true);
    await loadIssuer();
  };

  const closeIssuerModal = () => {
    if (issuerSaving) return;
    setIssuerModalOpen(false);
    setIssuerMessage('');
    loadIssuer();
  };

  const filteredAccounts = useMemo(() => {
    const needle = String(search || '').trim().toLowerCase();
    if (!needle) return accounts;
    return accounts.filter((row) =>
      `${row.companyName || ''} ${row.email || ''}`.toLowerCase().includes(needle)
    );
  }, [accounts, search]);

  const allFilteredSelected = useMemo(() => {
    if (!filteredAccounts.length) return false;
    return filteredAccounts.every((row) => selectedIds.has(row.companyId));
  }, [filteredAccounts, selectedIds]);

  const toggleSelect = (companyId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredAccounts.forEach((row) => next.delete(row.companyId));
      } else {
        filteredAccounts.forEach((row) => next.add(row.companyId));
      }
      return next;
    });
  };

  const reportBulkError = (msg) => {
    setBulkMessage(msg);
    onError?.(msg);
  };

  const runBulkUpdate = async ({ all = false } = {}) => {
    setBulkMessage('');
    const reason = String(bulkForm.reason || '').trim();
    if (!reason) {
      reportBulkError('Reason is required for bulk adjustments');
      return;
    }
    if (!all && selectedIds.size === 0) {
      reportBulkError('Select at least one company, or use Apply to all');
      return;
    }

    const payload = { reason };
    if (all) {
      payload.all = true;
    } else {
      payload.companyIds = [...selectedIds];
    }

    const leads = String(bulkForm.leadsFreeRemaining ?? '').trim();
    const mins = String(bulkForm.noteMinutesFreeRemaining ?? '').trim();
    const credits = String(bulkForm.creditBalance ?? '').trim();
    if (leads !== '') payload.leadsFreeRemaining = Number(leads);
    if (mins !== '') payload.noteMinutesFreeRemaining = Number(mins);
    if (credits !== '') payload.creditBalance = Number(credits);

    if (
      payload.leadsFreeRemaining === undefined &&
      payload.noteMinutesFreeRemaining === undefined &&
      payload.creditBalance === undefined
    ) {
      reportBulkError('Set at least one of: free credits, free mins, or credits');
      return;
    }

    const targetLabel = all
      ? `ALL ${accounts.length} companies`
      : `${selectedIds.size} selected compan${selectedIds.size === 1 ? 'y' : 'ies'}`;
    if (!window.confirm(`Apply bulk update to ${targetLabel}?`)) return;

    setBulkSaving(true);
    try {
      const data = await adminApi.bulkUpdateLeadsBillingAccounts(payload);
      if (data?.failedCount) {
        reportBulkError(`Updated ${data.updatedCount}, failed ${data.failedCount}`);
      } else {
        setBulkMessage(`Updated ${data?.updatedCount ?? 0} accounts`);
      }
      await loadAccounts();
      setBulkForm((prev) => ({ ...prev, reason: '' }));
    } catch (err) {
      reportBulkError(err?.response?.data?.message || err.message || 'Bulk update failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const filteredLedger = useMemo(() => {
    if (ledgerTab === 'recharge') {
      return ledger.filter((row) => RECHARGE_TYPES.has(String(row.entryType || '')));
    }
    return ledger.filter((row) => USAGE_TYPES.has(String(row.entryType || '')));
  }, [ledger, ledgerTab]);

  const saveAccount = async (companyId) => {
    const draft = edits[companyId] || {};
    if (!String(draft.reason || '').trim()) {
      onError?.('Reason is required for admin adjustments');
      return;
    }
    setSavingId(companyId);
    try {
      await adminApi.updateLeadsBillingAccount(companyId, {
        leadsFreeRemaining: Number(draft.leadsFreeRemaining),
        noteMinutesFreeRemaining: Number(draft.noteMinutesFreeRemaining),
        creditBalance: Number(draft.creditBalance),
        leadRate: draft.leadRate === '' ? null : Number(draft.leadRate),
        noteMinuteRate: draft.noteMinuteRate === '' ? null : Number(draft.noteMinuteRate),
        reason: String(draft.reason).trim(),
      });
      await loadAccounts();
    } catch (err) {
      onError?.(err?.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSavingId(null);
    }
  };

  const openLedger = async (row) => {
    setLedgerCompany({
      companyId: row.companyId,
      companyName: row.companyName || `Company #${row.companyId}`,
      email: row.email || '',
    });
    setLedgerTab('recharge');
    setLedgerOpen(true);
    setLedgerLoading(true);
    try {
      const data = await adminApi.fetchLeadsBillingLedger(row.companyId, { limit: 120 });
      setLedger(Array.isArray(data?.ledger) ? data.ledger : []);
    } catch (err) {
      onError?.(err?.response?.data?.message || err.message || 'Failed to load ledger');
      setLedger([]);
    } finally {
      setLedgerLoading(false);
    }
  };

  const closeLedger = () => {
    setLedgerOpen(false);
    setLedgerCompany(null);
    setLedger([]);
  };

  const openPaymentDetail = async (id) => {
    try {
      const data = await adminApi.fetchLeadsBillingPayment(id);
      setSelectedPayment(data?.payment || null);
    } catch (err) {
      onError?.(err?.response?.data?.message || err.message || 'Failed to load payment');
    }
  };

  return (
    <div className="admin-usage-tab">
      <div className="admin-inline-controls admin-usage-controls">
        <div className="control-group" style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className={`btn-secondary small ${subTab === 'accounts' ? 'active' : ''}`}
            onClick={() => setSubTab('accounts')}
          >
            Accounts
          </button>
          <button
            type="button"
            className={`btn-secondary small ${subTab === 'payments' ? 'active' : ''}`}
            onClick={() => setSubTab('payments')}
          >
            Payments ledger
          </button>
        </div>
        {subTab === 'accounts' ? (
          <>
            <div className="control-group">
              <label>Search company</label>
              <input
                type="text"
                value={search}
                placeholder="Name or email"
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="button" className="btn-secondary small" onClick={loadAccounts} disabled={loading}>
              Refresh
            </button>
          </>
        ) : (
          <>
            <div className="control-group">
              <label>Status</label>
              <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                <option value="">All</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <button
              type="button"
              className="btn-secondary small"
              onClick={loadPayments}
              disabled={paymentsLoading}
            >
              Refresh
            </button>
          </>
        )}
      </div>

      {metrics ? (
        <div className="admin-inline-controls" style={{ marginBottom: 12, gap: 16 }}>
          <span className="muted">Credits topped up: {formatMoney(metrics.creditsToppedUp)}</span>
          <span className="muted">Leads charged: {Number(metrics.leadsCharged || 0)}</span>
          <span className="muted">
            Note mins charged: {Number(metrics.noteMinutesCharged || 0).toFixed(2)}
          </span>
          {defaults ? (
            <span className="muted">
              Defaults: {defaults.defaultLeadsFree} leads / {defaults.defaultNoteMinutesFree} mins @ ₹
              {defaults.defaultLeadRate} credits/lead · ₹{defaults.defaultNoteMinuteRate}/min
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="dashboard-card admin-leads-platform-cta-card">
        <div className="admin-leads-platform-cta-main">
          <div className="admin-leads-platform-cta-copy">
            <h3>Platform configs</h3>
            <p className="muted">
              Invoice issuer, GST, joining grants, and referral rewards for Leads billing.
            </p>
          </div>
          <button
            type="button"
            className="btn-primary small"
            onClick={openIssuerModal}
            disabled={issuerLoading}
          >
            Configure
          </button>
        </div>
        <div className="admin-usage-platform-summary">
          <span className="admin-usage-platform-chip">
            <span className="muted">Org</span>
            <strong>{issuer.orgName || 'Not set'}</strong>
          </span>
          <span className="admin-usage-platform-chip">
            <span className="muted">GSTIN</span>
            <strong>{issuer.orgGstin || '-'}</strong>
          </span>
          <span className="admin-usage-platform-chip">
            <span className="muted">State</span>
            <strong>{formatStateLabel(issuer.orgStateCode) || '-'}</strong>
          </span>
          <span className="admin-usage-platform-chip">
            <span className="muted">GST</span>
            <strong>{formatGstPct(issuer.gstRate)}</strong>
          </span>
          <span className="admin-usage-platform-chip">
            <span className="muted">Joining grant</span>
            <strong>
              {issuer.defaultLeadsFree || '200'} leads / {issuer.defaultNoteMinutesFree || '60'} mins
            </strong>
          </span>
          <span className="admin-usage-platform-chip">
            <span className="muted">Referral</span>
            <strong>
              {issuer.referralLeadsFree || '50'} leads / {issuer.referralNoteMinutesFree || '30'} mins
            </strong>
          </span>
        </div>
      </div>

      {issuerModalOpen ? (
        <AdminModal
          wide
          title="Platform configs"
          subtitle="Seller details on BL invoices, GST, and Leads defaults for new companies."
          onClose={closeIssuerModal}
          footer={
            <>
              {issuerMessage ? <span className="muted admin-leads-platform-msg">{issuerMessage}</span> : <span />}
              <div className="admin-leads-platform-actions">
                <button
                  type="button"
                  className="btn-secondary small"
                  disabled={issuerSaving}
                  onClick={closeIssuerModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary small"
                  disabled={issuerSaving || issuerLoading}
                  onClick={saveIssuer}
                >
                  {issuerSaving ? 'Saving…' : 'Save configs'}
                </button>
              </div>
            </>
          }
        >
          {issuerLoading ? (
            <div className="muted">Loading platform configs…</div>
          ) : (
            <div className="admin-leads-platform-form">
              <section className="admin-leads-platform-section">
                <div className="admin-leads-platform-section-head">
                  <h4>Invoice issuer</h4>
                  <p className="muted">
                    Set GSTIN or GST state so same-state buyers get CGST/SGST instead of IGST.
                  </p>
                </div>
                <div className="admin-leads-platform-grid">
                  <PlatformField label="Org name">
                    <input
                      className="input"
                      value={issuer.orgName || ''}
                      onChange={(e) => updateIssuerField('orgName', e.target.value)}
                    />
                  </PlatformField>
                  <PlatformField label="Email">
                    <input
                      className="input"
                      type="email"
                      value={issuer.orgEmail || ''}
                      onChange={(e) => updateIssuerField('orgEmail', e.target.value)}
                    />
                  </PlatformField>
                  <PlatformField label="Phone">
                    <input
                      className="input"
                      value={issuer.orgPhone || ''}
                      onChange={(e) => updateIssuerField('orgPhone', e.target.value)}
                    />
                  </PlatformField>
                  <PlatformField label="Website">
                    <input
                      className="input"
                      value={issuer.orgWebsite || ''}
                      onChange={(e) => updateIssuerField('orgWebsite', e.target.value)}
                    />
                  </PlatformField>
                  <PlatformField label="GSTIN">
                    <input
                      className="input"
                      value={issuer.orgGstin || ''}
                      maxLength={15}
                      onChange={(e) =>
                        updateIssuerField(
                          'orgGstin',
                          String(e.target.value || '').toUpperCase().slice(0, 15)
                        )
                      }
                    />
                  </PlatformField>
                  <PlatformField label="State (GST)">
                    <select
                      className="input"
                      value={issuer.orgStateCode || ''}
                      disabled={Boolean(stateCodeFromGstin(issuer.orgGstin))}
                      onChange={(e) => updateIssuerField('orgStateCode', e.target.value)}
                    >
                      <option value="">Select state</option>
                      {GST_STATES.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.name} ({s.code})
                        </option>
                      ))}
                    </select>
                  </PlatformField>
                  <PlatformField label="GST rate">
                    <input
                      className="input"
                      type="number"
                      step="0.0001"
                      min="0"
                      value={issuer.gstRate}
                      onChange={(e) => updateIssuerField('gstRate', e.target.value)}
                      placeholder="0.18"
                    />
                  </PlatformField>
                  <PlatformField label="Address" full>
                    <textarea
                      className="input"
                      rows={2}
                      value={issuer.orgAddress || ''}
                      onChange={(e) => updateIssuerField('orgAddress', e.target.value)}
                    />
                  </PlatformField>
                </div>
              </section>

              <section className="admin-leads-platform-section">
                <div className="admin-leads-platform-section-head">
                  <h4>Joining grant</h4>
                  <p className="muted">Applied to new companies on welcome grant. Blank = code default.</p>
                </div>
                <div className="admin-leads-platform-grid admin-leads-platform-grid-4">
                  <PlatformField label="Free credits (joining)">
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="1"
                      value={issuer.defaultLeadsFree ?? ''}
                      placeholder="200"
                      onChange={(e) => updateIssuerField('defaultLeadsFree', e.target.value)}
                    />
                  </PlatformField>
                  <PlatformField label="Free note mins">
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="1"
                      value={issuer.defaultNoteMinutesFree ?? ''}
                      placeholder="60"
                      onChange={(e) => updateIssuerField('defaultNoteMinutesFree', e.target.value)}
                    />
                  </PlatformField>
                  <PlatformField label="Lead rate (credits/lead)">
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={issuer.defaultLeadRate ?? ''}
                      placeholder="1"
                      onChange={(e) => updateIssuerField('defaultLeadRate', e.target.value)}
                    />
                  </PlatformField>
                  <PlatformField label="Note min rate (₹)">
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={issuer.defaultNoteMinuteRate ?? ''}
                      placeholder="1.5"
                      onChange={(e) => updateIssuerField('defaultNoteMinuteRate', e.target.value)}
                    />
                  </PlatformField>
                </div>
              </section>

              <section className="admin-leads-platform-section">
                <div className="admin-leads-platform-section-head">
                  <h4>Referral reward</h4>
                  <p className="muted">
                    Granted to the inviter after the invitee&apos;s first paid top-up. Blank = code default.
                  </p>
                </div>
                <div className="admin-leads-platform-grid admin-leads-platform-grid-2">
                  <PlatformField label="Referral free credits">
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="1"
                      value={issuer.referralLeadsFree ?? ''}
                      placeholder="50"
                      onChange={(e) => updateIssuerField('referralLeadsFree', e.target.value)}
                    />
                  </PlatformField>
                  <PlatformField label="Referral free note mins">
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="1"
                      value={issuer.referralNoteMinutesFree ?? ''}
                      placeholder="30"
                      onChange={(e) => updateIssuerField('referralNoteMinutesFree', e.target.value)}
                    />
                  </PlatformField>
                </div>
              </section>
            </div>
          )}
        </AdminModal>
      ) : null}

      {subTab === 'accounts' ? (
        <>
        <div
          className="admin-inline-controls"
          style={{
            marginBottom: 12,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 10,
            padding: '10px 12px',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 8,
            background: 'rgba(0,0,0,0.02)',
          }}
        >
          <div className="control-group">
            <label>Bulk free credits</label>
            <input
              type="number"
              min="0"
              style={{ width: 90 }}
              placeholder="skip"
              value={bulkForm.leadsFreeRemaining}
              onChange={(e) =>
                setBulkForm((prev) => ({ ...prev, leadsFreeRemaining: e.target.value }))
              }
            />
          </div>
          <div className="control-group">
            <label>Bulk free mins</label>
            <input
              type="number"
              min="0"
              style={{ width: 90 }}
              placeholder="skip"
              value={bulkForm.noteMinutesFreeRemaining}
              onChange={(e) =>
                setBulkForm((prev) => ({
                  ...prev,
                  noteMinutesFreeRemaining: e.target.value,
                }))
              }
            />
          </div>
          <div className="control-group">
            <label>Bulk credits</label>
            <input
              type="number"
              min="0"
              style={{ width: 90 }}
              placeholder="skip"
              value={bulkForm.creditBalance}
              onChange={(e) =>
                setBulkForm((prev) => ({ ...prev, creditBalance: e.target.value }))
              }
            />
          </div>
          <div className="control-group" style={{ minWidth: 180 }}>
            <label>Bulk reason</label>
            <input
              type="text"
              placeholder="Required"
              value={bulkForm.reason}
              onChange={(e) => setBulkForm((prev) => ({ ...prev, reason: e.target.value }))}
            />
          </div>
          <button
            type="button"
            className="btn-primary small"
            disabled={bulkSaving || selectedIds.size === 0}
            onClick={() => runBulkUpdate({ all: false })}
          >
            Apply to selected ({selectedIds.size})
          </button>
          <button
            type="button"
            className="btn-secondary small"
            disabled={bulkSaving || accounts.length === 0}
            onClick={() => runBulkUpdate({ all: true })}
          >
            Apply to all ({accounts.length})
          </button>
          {bulkMessage ? (
            <span className="muted" style={{ flexBasis: '100%' }} role="status">
              {bulkMessage}
            </span>
          ) : null}
        </div>
        <div className="table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAllFiltered}
                    aria-label="Select all filtered companies"
                  />
                </th>
                <th>Company</th>
                <th>Free credits</th>
                <th>Free mins</th>
                <th>Credits</th>
                <th>Lead rate</th>
                <th>Note rate</th>
                <th>Used</th>
                <th>Reason</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((row) => {
                const draft = edits[row.companyId] || {};
                return (
                  <tr key={row.companyId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.companyId)}
                        onChange={() => toggleSelect(row.companyId)}
                        aria-label={`Select ${row.companyName || row.companyId}`}
                      />
                    </td>
                    <td>
                      <div>{row.companyName || `Company #${row.companyId}`}</div>
                      <div className="muted">{row.email}</div>
                    </td>
                    <td>
                      <input
                        style={{ width: 72 }}
                        value={draft.leadsFreeRemaining ?? ''}
                        onChange={(e) =>
                          setEdits((prev) => ({
                            ...prev,
                            [row.companyId]: { ...draft, leadsFreeRemaining: e.target.value },
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        style={{ width: 80 }}
                        value={draft.noteMinutesFreeRemaining ?? ''}
                        onChange={(e) =>
                          setEdits((prev) => ({
                            ...prev,
                            [row.companyId]: {
                              ...draft,
                              noteMinutesFreeRemaining: e.target.value,
                            },
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        style={{ width: 80 }}
                        value={draft.creditBalance ?? ''}
                        onChange={(e) =>
                          setEdits((prev) => ({
                            ...prev,
                            [row.companyId]: { ...draft, creditBalance: e.target.value },
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        style={{ width: 64 }}
                        placeholder="default"
                        value={draft.leadRate ?? ''}
                        onChange={(e) =>
                          setEdits((prev) => ({
                            ...prev,
                            [row.companyId]: { ...draft, leadRate: e.target.value },
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        style={{ width: 64 }}
                        placeholder="default"
                        value={draft.noteMinuteRate ?? ''}
                        onChange={(e) =>
                          setEdits((prev) => ({
                            ...prev,
                            [row.companyId]: { ...draft, noteMinuteRate: e.target.value },
                          }))
                        }
                      />
                    </td>
                    <td className="muted">
                      {row.leadsUsed} leads · {Number(row.noteMinutesUsed || 0).toFixed(2)} mins
                    </td>
                    <td>
                      <input
                        style={{ width: 140 }}
                        placeholder="Required reason"
                        value={draft.reason ?? ''}
                        onChange={(e) =>
                          setEdits((prev) => ({
                            ...prev,
                            [row.companyId]: { ...draft, reason: e.target.value },
                          }))
                        }
                      />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn-primary small"
                        disabled={savingId === row.companyId}
                        onClick={() => saveAccount(row.companyId)}
                      >
                        Save
                      </button>{' '}
                      <button
                        type="button"
                        className="btn-secondary small"
                        onClick={() => openLedger(row)}
                      >
                        Log
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!filteredAccounts.length ? (
                <tr>
                  <td colSpan={10} className="muted">
                    {loading ? 'Loading…' : 'No leads billing accounts.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        </>
      ) : (
        <div className="table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Company</th>
                <th>Credits</th>
                <th>Subtotal</th>
                <th>GST</th>
                <th>Breakup</th>
                <th>Total</th>
                <th>Status</th>
                <th>Invoice</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.paidAt || p.createdAt
                      ? new Date(p.paidAt || p.createdAt).toLocaleString()
                      : '-'}
                  </td>
                  <td>
                    <div>{p.companyName || `#${p.companyId}`}</div>
                    <div className="muted">{p.pack || 'custom'}</div>
                  </td>
                  <td>{p.credits}</td>
                  <td>{formatMoney(p.amount)}</td>
                  <td>{formatMoney(p.gstAmount)}</td>
                  <td className="muted">{formatGstBreakup(p.gstBreakup)}</td>
                  <td>{formatMoney(p.totalAmount)}</td>
                  <td>{p.status}</td>
                  <td>
                    {p.invoiceNumber || '-'}
                    {p.invoiceAvailable && p.invoiceUrl ? (
                      <>
                        {' '}
                        <a href={p.invoiceUrl} target="_blank" rel="noreferrer">
                          PDF
                        </a>
                      </>
                    ) : null}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-secondary small"
                      onClick={() => openPaymentDetail(p.id)}
                    >
                      Detail
                    </button>
                  </td>
                </tr>
              ))}
              {!payments.length ? (
                <tr>
                  <td colSpan={10} className="muted">
                    {paymentsLoading ? 'Loading…' : 'No payments yet.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      {ledgerOpen && ledgerCompany ? (
        <AdminModal
          wide
          title="Activity log"
          subtitle={`${ledgerCompany.companyName}${
            ledgerCompany.email ? ` · ${ledgerCompany.email}` : ''
          }`}
          onClose={closeLedger}
        >
          <div className="admin-leads-log-tabs" style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button
              type="button"
              className={`admin-leads-log-tab${ledgerTab === 'recharge' ? ' is-active' : ''}`}
              onClick={() => setLedgerTab('recharge')}
            >
              Recharge
            </button>
            <button
              type="button"
              className={`admin-leads-log-tab${ledgerTab === 'usage' ? ' is-active' : ''}`}
              onClick={() => setLedgerTab('usage')}
            >
              Usage
            </button>
          </div>
          {ledgerLoading ? (
            <p className="muted">Loading…</p>
          ) : (
            <div className="table-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Type</th>
                    <th>Δ leads</th>
                    <th>Δ mins</th>
                    <th>Δ credits</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLedger.map((row) => (
                    <tr key={row.id}>
                      <td>{new Date(row.createdAt).toLocaleString()}</td>
                      <td>{row.entryType}</td>
                      <td>{row.deltaLeadsFree}</td>
                      <td>{Number(row.deltaNoteMinutesFree || 0).toFixed(2)}</td>
                      <td>{Number(row.deltaCredits || 0).toFixed(2)}</td>
                      <td>{row.reason || '-'}</td>
                    </tr>
                  ))}
                  {!filteredLedger.length ? (
                    <tr>
                      <td colSpan={6} className="muted">
                        No {ledgerTab} rows.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </AdminModal>
      ) : null}

      {selectedPayment ? (
        <AdminModal
          title={`Payment #${selectedPayment.id}`}
          subtitle={
            selectedPayment.invoiceNumber ||
            selectedPayment.companyName ||
            `Company #${selectedPayment.companyId}`
          }
          onClose={() => setSelectedPayment(null)}
        >
          <div style={{ display: 'grid', gap: 10, fontSize: 14 }}>
            <div>
              <strong>{selectedPayment.companyName || `Company #${selectedPayment.companyId}`}</strong>
              {selectedPayment.email ? (
                <div className="muted">{selectedPayment.email}</div>
              ) : null}
            </div>
            <div>
              Status <strong>{selectedPayment.status}</strong>
              {selectedPayment.pack ? ` · Pack ${selectedPayment.pack}` : ''}
              {` · ${selectedPayment.credits} credits`}
            </div>
            <div>
              Subtotal {formatMoney(selectedPayment.amount)} · GST{' '}
              {formatMoney(selectedPayment.gstAmount)} · Total{' '}
              <strong>{formatMoney(selectedPayment.totalAmount)}</strong>
            </div>
            <div className="muted">Breakup: {formatGstBreakup(selectedPayment.gstBreakup)}</div>
            <div className="muted">
              Provider {(selectedPayment.provider || 'razorpay').toUpperCase()}
              {' · '}
              Order {selectedPayment.orderId || '-'}
              {selectedPayment.cfOrderId
                ? ` · PG order ${selectedPayment.cfOrderId}`
                : ''}
            </div>
            <div className="muted">
              Pay id {selectedPayment.cashfreePaymentId || '-'}
              {selectedPayment.paymentMethod ? ` · ${selectedPayment.paymentMethod}` : ''}
            </div>
            <div className="muted">
              Paid{' '}
              {selectedPayment.paidAt
                ? new Date(selectedPayment.paidAt).toLocaleString()
                : '-'}
              {selectedPayment.invoiceNumber ? ` · ${selectedPayment.invoiceNumber}` : ''}
            </div>
            {selectedPayment.invoiceAvailable && selectedPayment.invoiceUrl ? (
              <div style={{ marginTop: 4 }}>
                <a
                  className="btn-primary small"
                  href={selectedPayment.invoiceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open invoice PDF
                </a>
              </div>
            ) : null}
          </div>
        </AdminModal>
      ) : null}
    </div>
  );
}
